import {
  Box,
  IconButton,
  InputBase,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { Fragment, ReactNode, useRef, useState } from "react";

import { IcIcon } from "../IcIcon";

interface EnhancedTableToolbarProps<
  T extends Record<string, any> = Record<string, any>
> {
  actions?: Array<{
    icon: string;
    onClick?: (d: T) => any;
  }>;
  selectionActions?: Array<{
    icon: string;
    onClick?: (selected: T[]) => any;
  }>;
  resetSelected?: () => void;
  onSearchChange?: (val: string) => any;
  CustomToolbar?: ReactNode;
  searchValue?: string;
  selected: T[];
  title?: string;
  labelledData?: boolean;
}
const ToolbarTitle = styled("div")`
  min-width: 150px;
`;
const Spacer = styled("div")`
  flex: 1 1 100%;
`;

export const DataTableSearchComponent = ({
  searchValue,
  onSearchChange,
}: Pick<EnhancedTableToolbarProps, "searchValue" | "onSearchChange">) => {
  const ref = useRef<any>(null);
  const [show, setShow] = useState(false);

  if (typeof searchValue === "undefined") {
    return <></>;
  }
  const trueShow = show || searchValue.length > 0;
  return (
    <Paper
      square
      sx={{
        p: "2px 4px",
        display: "flex",
        alignItems: "center",
        width: trueShow ? "25ch" : "auto",
        borderBottom: trueShow ? "1px solid rgba(0,0,0,0.3)" : undefined,
        padding: 0,
        marginBottom: 0,
      }}
    >
      {(trueShow || searchValue.length !== 0) && (
        <InputBase
          onBlur={() => {
            if (searchValue.length === 0) {
              setShow(false);
            }
          }}
          ref={ref}
          sx={{ ml: 1, flex: 1 }}
          autoFocus
          value={searchValue}
          onChange={(e) => {
            onSearchChange?.(e.target.value);
          }}
        />
      )}
      <IconButton
        onClick={() => {
          setShow(!show);
        }}
        sx={{ p: "10px" }}
        aria-label="search"
      >
        <IcIcon icon="ic:baseline-search" />
      </IconButton>
    </Paper>
  );
};

export const DataTableToolbar = (props: EnhancedTableToolbarProps) => {
  const {
    title,
    selected,
    actions,
    labelledData,
    resetSelected,
    selectionActions,
    searchValue,
    onSearchChange,
    CustomToolbar,
  } = props;

  const bulkActions = selectionActions ?? actions;

  const allItems = [];
  selected.forEach((i: Record<string, any>) => {
    if (i?.label && i?.items) {
      allItems.push(...i?.items);
    }
  });

  const numSelected = labelledData ? allItems.length : selected.length;
  const runOnAll = async (onClick: (d: any) => any, treatAsMany?: boolean) => {
    if (treatAsMany) {
      onClick(selected);
      return;
    }
    await Promise.all(selected.map((d: any) => onClick(d)));
  };

  return (
    <Toolbar>
      <ToolbarTitle>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography variant="h6" id="tableTitle">
            {title}
          </Typography>
        )}
      </ToolbarTitle>
      <Spacer />
      {CustomToolbar}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        {numSelected > 0 && bulkActions ? (
          <>
            {bulkActions.map((a, i) => {
              return (
                <Fragment key={i}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      const handler = a.onClick;
                      if (!handler) return;
                      void runOnAll(handler, !!selectionActions).then(() => {
                        resetSelected?.();
                      });
                    }}
                    size="small"
                    sx={{ alignContent: "flex-end" }}
                  >
                    <IcIcon icon={a.icon} />
                  </IconButton>
                </Fragment>
              );
            })}
          </>
        ) : (
          <>
            <DataTableSearchComponent {...{ searchValue, onSearchChange }} />
          </>
        )}
      </Box>
    </Toolbar>
  );
};
